<script setup lang="ts">
import { computed, onMounted, PropType, ref } from "vue";
import { ProjectRole, TranslatedProduct } from "../../../types/PersologOnlineTypes";
import { useI18n } from "vue-i18n";
import {
    fetchDefaultRoleNamesForProduct,
    GROUP_CHART_FOR_361,
    GROUP_CHART_FOR_361_ITALIAN,
    fetchRoleTypes,
    JOB_PROFILE,
    JOB_PROFILE_SHORT,
    SHORTPROFILE_FOR_361,
    SHORTPROFILE_FOR_361_ITALIAN,
} from "../../../utils/ProjectUtils.ts";

const props = defineProps({
    roles: {
        type: Array as PropType<ProjectRole[]>,
        default: () => [],
    },
    product: {
        type: Object as PropType<TranslatedProduct>,
        default: () => ({}),
    },
    projectLanguage: {
        type: String,
        default: "de",
    },
});

const { t } = useI18n();

const emit = defineEmits(["valid", "update:roles"]);

/**
 * State to manage the editing status of each role individually.
 */
const nameEditing = ref<Record<string, { active: boolean; value: string }>>({});

/**
 * The roles that are available for the selected product and their corresponding translations
 * @type {type: string, name: string]} for each role, for displaying Checkbox components only.
 */
const allRoles = ref<{ type: string; name: string }[]>([]);
const defaultRoleNames = ref<Record<string, string>>({});

/**
 * The roles that are selected by the user during the configuration.
 * It is specifically used to display the checkboxes in the UI.
 */
const selectedRoles = ref<string[]>([]);

/**
 * Actual array of roles to be emitted to the parent component.
 */
const projectRoles = ref<ProjectRole[]>(props.roles);

function getDefaultRoleName(roleType: string): string {
    return (
        defaultRoleNames.value[roleType] ??
        defaultRoleNames.value[roleType.toLowerCase()] ??
        defaultRoleNames.value[roleType.toUpperCase()] ??
        roleType
    );
}

function isCustomizedRoleName(roleType: string, roleName: string): boolean {
    const trimmedName = roleName?.trim() ?? "";
    if (trimmedName === "") {
        return false;
    }
    return trimmedName !== getDefaultRoleName(roleType).trim();
}

const roleDescriptionText = computed(() => {
    const productNo = props.product.productNo;
    if (productNo === JOB_PROFILE || productNo === JOB_PROFILE_SHORT) {
        return t(`project-configuration.roles.description.${JOB_PROFILE}`);
    }
    return t(`project-configuration.roles.description.${SHORTPROFILE_FOR_361}`);
})

const isShortProfile361Family = computed(() => {
    const productNo = props.product.productNo;
    return [
        SHORTPROFILE_FOR_361,
        SHORTPROFILE_FOR_361_ITALIAN,
        GROUP_CHART_FOR_361,
        GROUP_CHART_FOR_361_ITALIAN,
    ].includes(productNo);
});

/**
 * Prepare the selected roles for the parent component.
 * Adds feedback recipient to beginning of the list.
 * @returns {ProjectRole[]} The selected roles.
 */
function getSelectedRoles() {
    projectRoles.value = [];
    for (const selectedRole of selectedRoles.value) {
        const role = allRoles.value.find((r) => r.type === selectedRole);
        if (role) {
            projectRoles.value.push({
                id: 0,
                role: role.type,
                // Persist only custom labels; defaults are represented as empty backend names.
                name: isCustomizedRoleName(role.type, role.name) ? role.name : "",
            });
        }
    }
    return projectRoles;
}

function updateRoleName(roleType: string, newName: string) {
    const role = allRoles.value.find((r) => r.type === roleType);
    if (role) {
        role.name = newName;
    }
    nameEditing.value[roleType].value = newName;
    nameEditing.value[roleType].active = false;
}

/**
 * Re-select the roles if user navigates back to this tab.
 */
function setRoles() {
    selectedRoles.value = [];
    projectRoles.value.forEach((role) => {
        const roleTranslation = allRoles.value.find((r) => r.type === role.role);
        if (roleTranslation) {
            selectedRoles.value.push(roleTranslation.type);
        }
    });
}

/**
 * Updates roles in parent component. E.g. ConfigureProject.vue
 */
function updateRoles() {
    emit("update:roles", getSelectedRoles().value);
}

const validate = () => {
    emit("valid", true);
    emit("update:roles", getSelectedRoles().value);
    return true;
};
defineExpose({ validate });

// Reselect roles if user navigates back to this tab.
onMounted(async () => {
    defaultRoleNames.value = await fetchDefaultRoleNamesForProduct(props.product.productNo, props.projectLanguage);

    allRoles.value = (await fetchRoleTypes(props.product.productNo)).map((role) => {
        const backendName = props.roles.find((r) => r.role === role.type)?.name?.trim() ?? "";
        const defaultName = getDefaultRoleName(role.type);

        return {
            type: role.type,
            name: backendName === "" ? defaultName : backendName,
        };
    });
    allRoles.value.forEach((role) => {
        nameEditing.value[role.type] = { active: false, value: role.name };
    });
    setRoles();
    // The feedback recipient is mandatory; add it without dropping what was chosen before going back.
    if (
        allRoles.value.some((role) => role.type === "FEEDBACK_RECIPIENT") &&
        !selectedRoles.value.includes("FEEDBACK_RECIPIENT")
    ) {
        selectedRoles.value.unshift("FEEDBACK_RECIPIENT");
    }
});
</script>

<template>
    <div class="card flex-row step-content">
        <h2 v-if="props.product">{{ props.product.productName }}</h2>
        <h4> {{roleDescriptionText }}</h4>
        <div class="flex flex-col gap-4">
            <div v-for="role of allRoles" :key="role.type" class="flex items-center">
                <Checkbox
                    v-model="selectedRoles"
                    :input-id="'configure-roles-tab_checkbox_' + role.type.toLowerCase()"
                    :name="role.name"
                    :value="role.type"
                    :aria-label="t('accessibility.checkboxes.selectRole') + ' ' + role.name"
                    class="checkbox-spacing"
                    @change="updateRoles"
                    :disabled="role.type === 'FEEDBACK_RECIPIENT'"
                />
                <Inplace
                    :id="`configuration-roles-tab_inplace_${role.type.toLowerCase()}`"
                    v-model:active="nameEditing[role.type].active"
                >
                    <template #display>
                        <label :id="'configuration-roles-tab_label_role-' + role.type.toLowerCase()" :for="role.type">
                            <span v-if="role.name !== getDefaultRoleName(role.type) && isShortProfile361Family">
                                {{ role.name }} ({{ getDefaultRoleName(role.type) }})
                            </span>
                            <span v-else>
                                {{ role.name }}
                            </span>
                        </label>
                        <i class="ml-1 text-xs pi pi-pencil"></i>
                    </template>
                    <template #content>
                        <span class="inline-flex items-center gap-2">
                            <FloatLabel variant="on">
                                <InputText
                                    :id="'configure-role-tab_input_role-name-' + role.type.toLowerCase()"
                                    size="small"
                                    v-model="nameEditing[role.type].value"
                                    autofocus
                                />
                                <label
                                    :for="'configure-role-tab_input_role-name-' + role.type.toLowerCase()"
                                    v-if="isShortProfile361Family"
                                >
                                    {{ getDefaultRoleName(role.type) }}
                                </label>
                            </FloatLabel>
                            <Button
                                :id="'configure-role-tab_btn_role-name-cancel-' + role.type.toLowerCase()"
                                icon="pi pi-times"
                                text
                                severity="danger"
                                @click="nameEditing[role.type].active = false"
                            />
                            <Button
                                :id="'configure-role-tab_btn_role-name-submit-' + role.type.toLowerCase()"
                                icon="pi pi-check"
                                text
                                severity="success"
                                @click="updateRoleName(role.type, nameEditing[role.type].value)"
                            />
                        </span>
                    </template>
                </Inplace>
            </div>
        </div>
    </div>
</template>

<style scoped>
.checkbox-spacing {
    margin-right: 10px;
}

.step-content {
    padding: 2rem;
    padding-top: 0rem;
    padding-bottom: 3rem;
}
</style>
